/**
 * useKillTeamDrum - the acting brother's Squad Mode plates for the drum (Squad Mode stage 7): the run
 * (`engine/actions/killTeamPlates`), why each is dimmed, which are lit, and what a press does. Null for anyone who is
 * no brother of a kill-team with an Oath, so most drums never change.
 *
 * Reads the RULE tokens (the positions seam: a `RULE_CONSUMERS` entry) for the support range. A press goes to the
 * kill-team's verbs at call time (`verbs`): they are declared in a later domain of the shell.
 *
 * `pressSquad(id)` is THE press of a Squad Mode plate, the drum's and the actor's strip's alike (plan modes-rework):
 * the verb, then - once it has TAKEN (an async refusal, a pool spent meanwhile, costs nothing) - its action charged
 * (`chargeForPress`: Squad Mode a Full Action, an ability its own, the rest free).
 */
import { useCallback, useMemo, useRef } from 'react';
import { brotherFacts, distanceIn } from '../engine/killTeam/facts';
import { cannotEnter } from '../engine/killTeam/eligibility';
import { abilityPlates } from '../engine/killTeam/strip';
import { killTeamDrum, killTeamEngaged } from '../engine/actions/killTeamPlates';
import { chargeForPress } from '../engine/actions/economy';
import type { ActionDef } from '../engine/actions/types';
import type { Combatant } from '../engine/roster/types';
import type { KillTeamsState } from './useKillTeams';
import type { KillTeamActions } from '../contexts/KillTeamsContext';

interface Args {
  actor: Combatant | null;
  roster: readonly Combatant[];
  /** The rule tokens (useShellGround). */
  tokens: Readonly<Record<string, { x: number; z: number }>>;
  killTeams: KillTeamsState | null;
  limit: boolean;
  isGm: boolean;
  /** The kill-team's verbs, read when a plate is pressed. */
  verbs: () => KillTeamActions | undefined;
  /** The advisory action economy's spender (half points), read at call time. */
  spendTurnRef?: { current?: ((id: string, points: number) => void) | null };
}

export interface KillTeamDrum {
  defs: ActionDef[];
  reasons: Record<string, string>;
  engaged: string[];
}

function drumOf(a: Omit<Args, 'verbs' | 'spendTurnRef'>): KillTeamDrum | null {
  const k = a.killTeams;
  const b =
    a.actor && k && brotherFacts(a.roster, k.teams, k.cohesion?.pools ?? null, a.actor.id, distanceIn(a.tokens));
  if (!b || !b.kt.oath) return null;
  const abilities = b.kt.oath.abilities;
  const plates = abilityPlates({
    brother: b.brother,
    abilities,
    pool: b.pool,
    sceneId: k.sceneId,
    limit: a.limit,
    isGm: a.isGm,
  });
  const ks = b.brother.state.killTeam;
  const facts = { inSquad: !!ks?.on, pending: !!ks?.pendingEntry, enterWhy: cannotEnter(b), plates };
  return { ...killTeamDrum(facts), engaged: killTeamEngaged(facts) };
}

export default function useKillTeamDrum(a: Args) {
  const { actor, roster, tokens, killTeams, limit, isGm } = a;
  const squad = useMemo(
    () => drumOf({ actor, roster, tokens, killTeams, limit, isGm }),
    [actor, roster, tokens, killTeams, limit, isGm],
  );
  const live = useRef({ ...a, squad });
  live.current = { ...a, squad };
  const pressSquad = useCallback((id: string): Promise<boolean> => {
    const { actor: brother, verbs, spendTurnRef, squad: run } = live.current;
    const def = run?.defs.find((d) => d.id === id);
    const v = verbs();
    if (!brother || !v || !def?.killTeam) return Promise.resolve(false);
    return verbOf(v, brother.id, def.killTeam)().then((took) => {
      if (took) spendTurnRef?.current?.(brother.id, chargeForPress(def, false));
      return took;
    });
  }, []);
  return { squad, pressSquad };
}

/** The kill-team's verb a plate names. */
function verbOf(v: KillTeamActions, id: string, kt: NonNullable<ActionDef['killTeam']>): () => Promise<boolean> {
  const press = {
    enter: () => v.enter(id),
    challenge: () => v.challenge(id),
    leave: () => v.leave(id),
    start: () => v.start(id, kt.abilityId ?? ''),
    join: () => v.join(id, kt.key ?? ''),
  };
  return press[kt.verb];
}
